import React, { useState } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  ScrollView,
  StyleSheet,
  Pressable,
  Platform,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RootStackParamList } from '../navigation/stackTypes';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { UserSessionAttributes } from '../models/user-session-model';
import RoleService from '../services/role-service';
import FileUploaderTest from '../components/admin/FileUploaderTest';
import UserManagement from '../components/admin/UserManagement';

type AdminScreenNavigationProp = NativeStackNavigationProp<
  RootStackParamList,
  'AdminScreen'
>;

interface AdminScreenProps {
  navigation: AdminScreenNavigationProp;
}

type AdminFeature = 
  | 'dashboard' 
  | 'file-uploader'
  | 'user-management'
  | 'system-logs'
  | 'analytics';

interface AdminMenuItem {
  id: AdminFeature;
  title: string;
  description: string;
  icon: keyof typeof MaterialIcons.glyphMap;
  component?: React.ComponentType<any>;
  available: boolean;
}

const AdminScreen: React.FC<AdminScreenProps> = ({ navigation }) => {
  const userSession: UserSessionAttributes | undefined = useSelector(
    (state: RootState) => state.user?.session
  );

  const [selectedFeature, setSelectedFeature] = useState<AdminFeature | null>(null);

  // Check if user is admin
  if (!RoleService.isAdmin(userSession)) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.container}>
          <View style={styles.unauthorizedContainer}>
            <MaterialIcons name="block" size={64} color="#FF4444" />
            <Text style={styles.unauthorizedTitle}>Access Denied</Text>
            <Text style={styles.unauthorizedText}>
              You don't have permission to access this area.
            </Text>
            <Pressable
              style={styles.backButton}
              onPress={() => navigation.goBack()}
            >
              <Text style={styles.backButtonText}>Go Back</Text>
            </Pressable>
          </View>
        </View>
      </SafeAreaView>
    );
  }

  const adminMenuItems: AdminMenuItem[] = [
    {
      id: 'file-uploader',
      title: 'File Uploader Test',
      description: 'Upload, download, and delete test files',
      icon: 'cloud-upload',
      component: FileUploaderTest,
      available: true,
    },
    {
      id: 'user-management',
      title: 'User Management',
      description: 'Manage users, roles, and permissions',
      icon: 'people',
      component: UserManagement,
      available: true,
    },
    {
      id: 'system-logs',
      title: 'System Logs',
      description: 'View and analyze system logs',
      icon: 'article',
      available: false,
    },
    {
      id: 'analytics',
      title: 'Analytics Dashboard',
      description: 'View usage statistics and metrics',
      icon: 'analytics',
      available: false,
    },
  ];

  const handleFeatureSelect = (featureId: AdminFeature) => {
    const feature = adminMenuItems.find(item => item.id === featureId);
    if (feature?.available) {
      setSelectedFeature(featureId);
    }
  };

  const handleBackToDashboard = () => {
    setSelectedFeature(null);
  };

  // Render selected feature
  if (selectedFeature) {
    const feature = adminMenuItems.find(item => item.id === selectedFeature);
    const FeatureComponent = feature?.component;

    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.container}>
          {/* Header */}
          <View style={styles.header}>
            <Pressable
              onPress={handleBackToDashboard}
              style={styles.backIconButton}
            >
              <MaterialIcons name="arrow-back" size={24} color="white" />
            </Pressable>
            <View style={styles.headerTitleContainer}>
              <Text style={styles.headerTitle}>{feature?.title}</Text>
              <Text style={styles.headerSubtitle}>{feature?.description}</Text>
            </View>
          </View>

          {/* Feature Content */}
          <View style={styles.featureContainer}>
            {FeatureComponent && <FeatureComponent navigation={navigation} />}
          </View>
        </View>
      </SafeAreaView>
    );
  }

  // Render admin dashboard
  return (
    <SafeAreaView style={styles.safeArea}>
      <View style={styles.container}>
        {/* Header */}
        <View style={styles.header}>
          <Pressable
            onPress={() => navigation.goBack()}
            style={styles.backIconButton}
          >
            <MaterialIcons name="arrow-back" size={24} color="white" />
          </Pressable>
          <View style={styles.headerTitleContainer}>
            <Text style={styles.headerTitle}>Admin Control Panel</Text>
            <Text style={styles.headerSubtitle}>
              Manage and test system features
            </Text>
          </View>
        </View>

        {/* Admin Dashboard */}
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollViewContent}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.dashboardContainer}>
            {/* Welcome Card */}
            <View style={styles.welcomeCard}>
              <MaterialIcons name="admin-panel-settings" size={48} color="#95F5AA" />
              <Text style={styles.welcomeTitle}>
                Welcome, {userSession?.first_name || 'Admin'}
              </Text>
              <Text style={styles.welcomeText}>
                Access administrative tools and system tests below
              </Text>
            </View>

            {/* Admin Menu Grid */}
            <View style={styles.menuGrid}>
              {adminMenuItems.map((item) => (
                <Pressable
                  key={item.id}
                  style={({ pressed }) => [
                    styles.menuCard,
                    !item.available && styles.menuCardDisabled,
                    pressed && item.available && styles.menuCardPressed,
                  ]}
                  onPress={() => handleFeatureSelect(item.id)}
                  disabled={!item.available}
                >
                  <View style={styles.menuCardIconContainer}>
                    <MaterialIcons
                      name={item.icon}
                      size={32}
                      color={item.available ? '#95F5AA' : '#666'}
                    />
                    {!item.available && (
                      <View style={styles.comingSoonBadge}>
                        <Text style={styles.comingSoonText}>Soon</Text>
                      </View>
                    )}
                  </View>
                  <Text
                    style={[
                      styles.menuCardTitle,
                      !item.available && styles.menuCardTitleDisabled,
                    ]}
                  >
                    {item.title}
                  </Text>
                  <Text
                    style={[
                      styles.menuCardDescription,
                      !item.available && styles.menuCardDescriptionDisabled,
                    ]}
                  >
                    {item.description}
                  </Text>
                  {item.available && (
                    <View style={styles.menuCardArrow}>
                      <MaterialIcons
                        name="arrow-forward"
                        size={20}
                        color="#95F5AA"
                      />
                    </View>
                  )}
                </Pressable>
              ))}
            </View>

            {/* Info Section */}
            <View style={styles.infoSection}>
              <MaterialIcons name="info-outline" size={20} color="#00D9FF" />
              <Text style={styles.infoText}>
                More admin features are in development and will be added soon.
              </Text>
            </View>
          </View>
        </ScrollView>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#1E1E1E',
  },
  container: {
    flex: 1,
    backgroundColor: '#1E1E1E',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 16,
    backgroundColor: '#2A2A2A',
    borderBottomWidth: 1,
    borderBottomColor: '#00D9FF33',
  },
  backIconButton: {
    padding: 8,
    marginRight: 8,
  },
  headerTitleContainer: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: 'white',
  },
  headerSubtitle: {
    fontSize: 14,
    color: '#AAA',
    marginTop: 2,
  },
  scrollView: {
    flex: 1,
  },
  scrollViewContent: {
    padding: 16,
  },
  dashboardContainer: {
    gap: 20,
  },
  welcomeCard: {
    backgroundColor: '#2A2A2A',
    borderRadius: 16,
    padding: 24,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#00D9FF33',
  },
  welcomeTitle: {
    fontSize: 22,
    fontWeight: '600',
    color: 'white',
    marginTop: 16,
  },
  welcomeText: {
    fontSize: 14,
    color: '#AAA',
    marginTop: 8,
    textAlign: 'center',
  },
  menuGrid: {
    gap: 16,
  },
  menuCard: {
    backgroundColor: '#2A2A2A',
    borderRadius: 12,
    padding: 20,
    borderWidth: 1,
    borderColor: '#00D9FF33',
    position: 'relative',
  },
  menuCardDisabled: {
    opacity: 0.5,
    borderColor: '#333',
  },
  menuCardPressed: {
    backgroundColor: '#333',
    transform: [{ scale: 0.98 }],
  },
  menuCardIconContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  comingSoonBadge: {
    backgroundColor: '#FF8C00',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginLeft: 12,
  },
  comingSoonText: {
    fontSize: 10,
    fontWeight: '600',
    color: 'white',
  },
  menuCardTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: 'white',
    marginBottom: 8,
  },
  menuCardTitleDisabled: {
    color: '#666',
  },
  menuCardDescription: {
    fontSize: 14,
    color: '#AAA',
    lineHeight: 20,
  },
  menuCardDescriptionDisabled: {
    color: '#555',
  },
  menuCardArrow: {
    position: 'absolute',
    top: 20,
    right: 20,
  },
  infoSection: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#2A2A2A',
    borderRadius: 12,
    padding: 16,
    gap: 12,
    borderWidth: 1,
    borderColor: '#00D9FF33',
  },
  infoText: {
    flex: 1,
    fontSize: 14,
    color: '#AAA',
    lineHeight: 20,
  },
  unauthorizedContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  unauthorizedTitle: {
    fontSize: 24,
    fontWeight: '600',
    color: 'white',
    marginTop: 16,
  },
  unauthorizedText: {
    fontSize: 16,
    color: '#AAA',
    textAlign: 'center',
    marginTop: 8,
  },
  backButton: {
    backgroundColor: '#00D9FF',
    paddingHorizontal: 32,
    paddingVertical: 12,
    borderRadius: 8,
    marginTop: 24,
  },
  backButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1E1E1E',
  },
  featureContainer: {
    flex: 1,
  },
});

export default AdminScreen;

